export function sanitizeABN(abn: string | number): string {
  return String(abn).replace(/\D/g, '')
}

// ABN Validation format: https://abr.business.gov.au/Help/AbnFormat
export function validateABN(abn: string | number) {
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]
  const abnString = sanitizeABN(abn)

  if (abnString.length !== 11) {
    return false
  }

  const sum = weights.reduce((acc, weight, index) => {
    const digit = Number(abnString[index]) - (index === 0 ? 1 : 0)
    return acc + weight * digit
  }, 0)

  const isValid = (sum % 89) === 0
  return isValid
}

// The NZBN is a GLN GTIN-13 (same standards used to create bar codes)
// https://www.gs1.org/services/how-calculate-check-digit-manually
export function validateNZBN(nzbn: string): boolean {
  if (nzbn.toString().length !== 13) {
    return false
  }

  let sum = 0
  for (let i = 0; i < 12; i++) {
    // Multiply by 1 if it's an even index, else multiply by 3
    sum += (i % 2 === 0 ? 1 : 3) * parseInt(nzbn[i])
  }

  const checkDigit = (10 - (sum % 10)) % 10
  const valid = checkDigit === parseInt(nzbn[12])
  return valid
}

// A CRN is a unique identifier for UK companies.
// It is 8 characters long: 8 digits or 2 letters from a list of combinations followed by 6 digits
// https://www.yourcompanyformations.co.uk/blog/company-registration-number-crn-number-explained/
export function validateCRN(crn: string): boolean {
  const validationRegex = /^(((AC|CE|CS|FC|FE|GE|GS|IC|LP|NC|NF|NI|NL|NO|NP|OC|OE|PC|R0|RC|SA|SC|SE|SF|SG|SI|SL|SO|SR|SZ|ZC|\d{2})\d{6})|((IP|SP|RS)[A-Z\d]{6})|(SL\d{5}[\dA]))$/
  return validationRegex.test(crn)
}

export function validateEIN(ein: string): boolean {
  const validationRegex = /^[0-9]{9}$/
  return validationRegex.test(ein)
}

export function validateBusinessIdentifier(regionCode: string, businessIdentifier: string) {
  switch (regionCode) {
    case 'AU':
      return validateABN(businessIdentifier)
    case 'NZ':
      return validateNZBN(businessIdentifier)
    case 'GB':
      return validateCRN(businessIdentifier)
    case 'US':
      return validateEIN(businessIdentifier)
    default:
      return false
  }
}

export function getAgencyCodeInfoFromCountryCode(countryCode: string): { label: string, lookupUrl: string } {
  switch (countryCode) {
    case 'AU':
      return { label: 'ABN', lookupUrl: 'https://abr.business.gov.au/' }
    case 'NZ':
      return { label: 'NZBN', lookupUrl: 'https://www.nzbn.govt.nz/' }
    case 'GB':
      return { label: 'CRN', lookupUrl: 'https://find-and-update.company-information.service.gov.uk/' }
    case 'US':
      return { label: 'EIN', lookupUrl: '' }
    default:
      return { label: '', lookupUrl: '' }
  }
}
